import React, { useMemo } from 'react';
import _ from 'lodash';
import { withLocalize } from 'react-localize-redux';
import { useLocalize } from '../../hooks';
var externalLinkTextId = 'externalLink';
/**
 * Gets the subdirectory prefix (e.g., '/TBFE-1955-2' or '').
 * @returns The subdirectory prefix (e.g., '/TBFE-1955-2' or '').
 */

var getSubdirectory = function getSubdirectory() {
  if (typeof window !== 'undefined' && window.__TB_SUBDIRECTORY__) {
    var subdirectory = window.__TB_SUBDIRECTORY__;
    return subdirectory.endsWith('/') ? subdirectory.slice(0, -1) : subdirectory;
  }

  return '';
};
/**
 * Subdomain labels for hosts that serve RestHub.
 * Matches REACT_APP_TRADING_API_URL / TB_RESTHUB_URL hosts such as devapi.tradingblock.com.
 * Links to this host must keep their original path; injecting the feature branch segment would break routes like /Documents/Agreement/...
 */


var RESTHUB_HOST_FIRST_LABELS = new Set(['api', 'betaapi', 'devapi', 'vtapi']);
/** True when hostname is e.g. devapi.tradingblock.com (first segment listed above). */

var isRestHubHostname = function isRestHubHostname(hostname) {
  var firstLabel = hostname.split('.')[0];
  return RESTHUB_HOST_FIRST_LABELS.has(firstLabel);
};
/**
 * For feature branch deploys, prepend the branch segment to links that target other TB/MB Docker containers so Nginx can route them.
 * Skips RestHub host (see RESTHUB_HOST_FIRST_LABELS) and non-TB/MB URLs.
 * @param url - The external URL (e.g., 'https://dashboard.tradingblock.com/auth' or 'https://application.tradingblock.com/apply').
 * @returns The URL with branch prefix in the path when applicable (e.g., 'https://dashboard.tradingblock.com/TBFE-1955-2/auth').
 */


var getExternalUrlWithBranchPrefix = function getExternalUrlWithBranchPrefix(url) {
  // If not an absolute URL, return as-is (shouldn't happen, but be safe).
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return url;
  }

  try {
    var urlObj = new URL(url);
    var hostname = urlObj.hostname.toLowerCase(); // Check if this is a nginx-handled domain (tradingblock.com subdomains, excluding legacy).
    // Domains like dashboard.tradingblock.com, devdashboard.tradingblock.com, application.tradingblock.com, etc.

    var isNginxHandledDomain = (hostname.endsWith('.tradingblock.com') || hostname.endsWith('.moneyblock.com')) && !hostname.startsWith('legacy.') && !hostname.startsWith('www.'); // If not a nginx-handled domain, return as-is.

    if (!isNginxHandledDomain) {
      return url;
    } // RestHub: never add the branch to the path (paths are not Nginx routes).


    if (isRestHubHostname(hostname)) {
      return url;
    } // Get subdirectory prefix.


    var subdirectory = getSubdirectory(); // If no subdirectory, return as-is (main branch deploy or local dev).

    if (!subdirectory) {
      return url;
    } // Check if path already contains the subdirectory prefix to avoid double prefix.
    // This can happen if the URL was already processed or came from a source that included it.


    if (urlObj.pathname.startsWith(subdirectory)) {
      // Path already has the prefix, return as-is.
      return url;
    } // Add branch prefix to the path.


    var pathWithPrefix = subdirectory + urlObj.pathname + urlObj.search + urlObj.hash; // Reconstruct URL with new path.

    return "".concat(urlObj.protocol, "//").concat(urlObj.host).concat(pathWithPrefix);
  } catch (error) {
    // If URL parsing fails, return as-is.
    console.warn('[ExternalLink] Failed to parse URL:', url, error);
    return url;
  }
};

var ExternalLinkContent = function ExternalLinkContent(_ref) {
  var href = _ref.href,
      className = _ref.className,
      onClick = _ref.onClick,
      translate = _ref.translate,
      children = _ref.children,
      styleOverride = _ref.styleOverride;

  var _useLocalize = useLocalize(),
      getTranslationId = _useLocalize.getTranslationId;

  var ariaLabel = useMemo(function () {
    var val = translate(getTranslationId({
      id: externalLinkTextId
    }));
    return _.isString(val) ? val : _.startCase(externalLinkTextId);
  }, [translate, getTranslationId]);
  var finalHref = useMemo(function () {
    return getExternalUrlWithBranchPrefix(href);
  }, [href]);
  return React.createElement("a", {
    href: finalHref,
    className: className,
    target: "_blank",
    rel: "noopener noreferrer",
    "aria-label": "(".concat(ariaLabel, ")"),
    onClick: onClick,
    style: styleOverride
  }, children);
};

export var ExternalLink = withLocalize(ExternalLinkContent);